import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import trackEvents from '../../../constants/track-events'
import { useNotificationContext } from '../../../context'
import { query } from '../../../graphql'
import { notifyBugsnag } from '../../../helpers'
import { trackEvent } from '../../../helpers/analytics'
import { useTalentStorage } from '../../pricing/hooks'
import {
  LIMIT_DIALOG_ACTIONS,
  usePricingDialog
} from '../../pricing/pricing-dialog-context'
import { EVALUATION_TYPES } from '../utils'

export const usePdfGeneration = () => {
  const { t, i18n } = useTranslation()
  const { error } = useNotificationContext()
  const { pending, isLocked, isPendingId, isOwnerLocked, isPendingOwner } =
    useTalentStorage({ fetch: 'auto' })
  const { openLimitDialog } = usePricingDialog()

  const generateMatchingReport = useCallback(
    async (career, talent) => {
      // Kein PDF für gesperrte Talente, gesperrt wird je Person. Solange der
      // Talentspeicher nicht gezählt ist oder das Talent noch nachlädt,
      // passiert nichts, gesperrt zeigt den Hinweis.
      const owner = talent?.owner
      if (isLocked(talent?.id) || (!!owner && !!isOwnerLocked?.(owner))) {
        if (
          !pending &&
          !isPendingId?.(talent?.id) &&
          !(owner && isPendingOwner?.(owner))
        ) {
          openLimitDialog('talentStorage', {
            action: LIMIT_DIALOG_ACTIONS.INFO,
            source: 'discover_matching_pdf'
          })
        }
        return
      }

      trackEvent(trackEvents.DISCOVER_PRESSED_DOWNLOAD_MATCHING_PROFILE)

      const { diagnostic_version } = career
      let pdfDocument = null

      try {
        const variables = { career_id: career.id, language: i18n.language }

        if (diagnostic_version === 2) {
          variables.space_id = talent.id
          variables.type = EVALUATION_TYPES.MATCHINGPROFILE
        }

        pdfDocument = window.open('', '_blank')
        if (!pdfDocument) {
          throw new Error('Popup blocked or failed to open')
        }

        pdfDocument.document.writeln(t('pdf_preparation'))

        const pdf = await query({ query: 'generatePdf', variables })

        if (!pdf || pdf === 'undefined') {
          throw new Error('Invalid response from generatePdf query')
        }

        let pdfData
        try {
          pdfData = JSON.parse(pdf)
        } catch (parseError) {
          console.error(
            'PDF generation JSON parse error:',
            parseError,
            'Response:',
            pdf
          )
          throw new Error(`Failed to parse PDF response: ${parseError.message}`)
        }

        if (!pdfData?.headers?.Location) {
          throw new Error('Invalid PDF response')
        }

        pdfDocument.location.href = pdfData.headers.Location
      } catch (err) {
        notifyBugsnag(err)
        console.error('PDF generation failed:', err)

        if (pdfDocument && !pdfDocument.closed) {
          pdfDocument.close()
        }

        error?.(t('discover.talent_match_section.pdf_generation_error'))
      }
    },
    [
      t,
      i18n.language,
      error,
      isLocked,
      isPendingId,
      isOwnerLocked,
      isPendingOwner,
      pending,
      openLimitDialog
    ]
  )

  return { generateMatchingReport }
}
